// Email + password flows with 6-digit codes (WEB_PLAN.md §4.2), mirroring GroundTeam/Sync/SyncCenter.swift
// (signIn, signUp, verifySignUp → finishConfirmedSignUp, resendSignUpCode, sendPasswordReset, resetPassword →
// finishPasswordReset, changePassword, signInWithApple / signInWithGoogle). Every function throws supabase-js
// errors (or ./errors classes) that `authErrorMessage` words like iOS.
//
// There is deliberately no emailed sign-in code (`signInWithOtp`): with open sign-ups it would confirm an account
// someone else registered unconfirmed, with their password. Codes only confirm a sign-up or reset a password, and
// both end with the person's own password saved.

import { supabase } from '@/supabase';
import { ACCOUNT_EXISTS_TEXT, authErrorCode, isSessionFailure, ServerError } from './errors';
import {
  beginFinishingSignIn,
  clearVerification,
  endFinishingSignIn,
  keepFinishingForNotice,
  markCodeResent,
  releaseNoticeHold,
  setSharedProfileName,
  startVerification,
  takePendingPassword,
} from './pending';

/** Trimmed, lower-cased address (SyncCenter.normalized). */
export function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

/** A usable address: something@something, no spaces (AuthInput.email). */
export function isEmailLike(raw: string): boolean {
  const t = normalizeEmail(raw);
  const parts = t.split('@');
  return parts.length === 2 && parts[0] !== '' && parts[1] !== '' && !t.includes(' ');
}

/** Digits only (codes are pasted with spaces or dashes), at most 10 in case the code length is ever raised. */
export function codeDigits(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 10);
}

export function isCompleteCode(code: string): boolean {
  return code.length >= 6 && code.length <= 10;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Saves `password` for the signed-in session. "Same password" counts as saved: the account already has exactly
 * this password, which is what the person wanted (SyncCenter.savePassword).
 */
async function savePassword(password: string): Promise<void> {
  const { error } = await supabase.auth.updateUser({ password });
  if (error && authErrorCode(error) !== 'same_password') throw error;
}

async function signOutLocal(): Promise<void> {
  try {
    await supabase.auth.signOut({ scope: 'local' });
  } catch {
    // The local session is removed either way.
  }
}

// ---------------------------------------------------------------------------------------------------------------------
// Sign in
// ---------------------------------------------------------------------------------------------------------------------

export type SignInResult = 'signedIn' | 'needsVerification';

/**
 * Email + password. An account that was never confirmed (`email_not_confirmed`) gets a fresh code and the password
 * is kept for the verify step, which saves it again once the code confirms the account. If sending fails (e.g. too
 * soon after the last one) an earlier code still works and the code screen can resend.
 */
export async function signInWithEmail(rawEmail: string, password: string): Promise<SignInResult> {
  const email = normalizeEmail(rawEmail);
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (!error) return 'signedIn';
  if (authErrorCode(error) === 'email_not_confirmed') {
    await supabase.auth.resend({ type: 'signup', email }).catch(() => undefined);
    startVerification(email, password, 'signIn');
    return 'needsVerification';
  }
  throw error;
}

// ---------------------------------------------------------------------------------------------------------------------
// Create account + verify
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Creates an email + password account. With confirmations on (the project's setting) there is no session yet and a
 * 6-digit code was emailed: finish with `verifySignUp`. GoTrue answers a sign-up for an address that already has a
 * confirmed account with a look-alike user whose `identities` list is empty (and sends nothing): say so now instead
 * of waiting for a code that never comes.
 */
export async function signUpWithEmail(rawEmail: string, password: string): Promise<SignUpResult> {
  const email = normalizeEmail(rawEmail);
  // Held from the start: a server that confirms sign-ups by itself answers with a session, which must not count as
  // signed in before its password is saved again below.
  beginFinishingSignIn();
  let holdGate = false;
  try {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
    if (data.session) {
      // Confirmed right after creating the account, which drops the password like any confirmation, so it's saved
      // again here as well (SyncCenter.finishSignUp).
      const outcome = await saveSignUpPassword(password);
      holdGate = outcome === 'unsaved';
      return outcome;
    }
    if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      throw new ServerError(422, 'user_already_exists', ACCOUNT_EXISTS_TEXT);
    }
    startVerification(email, password, 'signUp');
    return 'needsVerification';
  } finally {
    if (holdGate) keepFinishingForNotice();
    else endFinishingSignIn();
  }
}

/**
 * 'needsVerification': a code was emailed (the project's setting). 'saved' / 'unsaved': the server confirmed the
 * account by itself and it is signed in; with 'unsaved' the finishing gate is still held (see `verifySignUp`).
 */
export type SignUpResult = 'needsVerification' | 'saved' | 'unsaved';

/**
 * Saves the sign-up password right after confirmation; one retry after a second (SyncCenter.finishConfirmedSignUp).
 * A refused session (401) signs out and throws: there is nothing to stay signed in with. Otherwise a failure leaves
 * the person signed in (the account is theirs) and returns 'unsaved'.
 */
async function saveSignUpPassword(password: string): Promise<'saved' | 'unsaved'> {
  let failure: unknown = null;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (attempt > 0) await sleep(1000);
    try {
      await savePassword(password);
      return 'saved';
    } catch (e) {
      failure = e;
      if (isSessionFailure(e)) break;
    }
  }
  if (isSessionFailure(failure)) {
    await signOutLocal();
    throw failure;
  }
  return 'unsaved';
}

/**
 * Confirms an email sign-up with the emailed code (`verifyOtp` type 'email', not 'signup') and signs in, then saves
 * the password chosen at sign-up again. Holds the finishing gate throughout, so the session isn't treated as signed
 * in (and the verify screen isn't navigated away) until the password step is over.
 *
 * Returns 'saved', or 'unsaved' (confirmed and signed in, password not saved: show PASSWORD_NOT_SAVED_TEXT). While
 * 'unsaved' the gate is still HELD so the screen can show the notice; call `completeSignIn()` when the person
 * continues or leaves the notice (it is idempotent). On a thrown error the gate is released.
 */
export async function verifySignUp(rawEmail: string, code: string): Promise<'saved' | 'unsaved'> {
  const email = normalizeEmail(rawEmail);
  beginFinishingSignIn();
  let holdGate = false;
  try {
    const { error } = await supabase.auth.verifyOtp({ email, token: codeDigits(code), type: 'email' });
    if (error) throw error;
    const password = takePendingPassword(email);
    clearVerification();
    // Without the password at hand (never expected: the screen needs the pending address) the account stays
    // confirmed without one, like iOS `noPasswordNotice`.
    const outcome = password === null ? 'unsaved' : await saveSignUpPassword(password);
    holdGate = outcome === 'unsaved';
    return outcome;
  } finally {
    if (holdGate) keepFinishingForNotice();
    else endFinishingSignIn();
  }
}

/**
 * The person is done with the "password wasn't saved" notice (Continue, or the notice left the screen): count the
 * session as signed in now. Safe to call more than once; it only releases the hold `verifySignUp` /
 * `signUpWithEmail` kept, never another flow's.
 */
export function completeSignIn(): void {
  releaseNoticeHold();
}

/**
 * Emails a new confirmation code for a sign-up that isn't confirmed yet (GoTrue's resend never creates an account
 * or signs in; it answers 200 and sends nothing for a confirmed or unknown address).
 */
export async function resendSignUpCode(rawEmail: string): Promise<void> {
  const { error } = await supabase.auth.resend({ type: 'signup', email: normalizeEmail(rawEmail) });
  if (error) throw error;
  markCodeResent();
}

// ---------------------------------------------------------------------------------------------------------------------
// Forgot password
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Emails a reset code. The server answers the same whether or not the address has an account, so this can't be
 * used to find out who has one.
 */
export async function sendPasswordReset(rawEmail: string): Promise<void> {
  const { error } = await supabase.auth.resetPasswordForEmail(normalizeEmail(rawEmail));
  if (error) throw error;
}

/**
 * Checks the emailed reset code, sets the new password and signs in. If the new password can't be saved the
 * browser signs out again so no half-reset session stays (the used code then needs "Resend code").
 */
export async function resetPassword(rawEmail: string, code: string, newPassword: string): Promise<void> {
  const email = normalizeEmail(rawEmail);
  beginFinishingSignIn();
  try {
    const { error } = await supabase.auth.verifyOtp({ email, token: codeDigits(code), type: 'recovery' });
    if (error) throw error;
    try {
      await savePassword(newPassword);
    } catch (e) {
      await signOutLocal();
      throw e;
    }
    clearVerification();
  } finally {
    endFinishingSignIn();
  }
}

// ---------------------------------------------------------------------------------------------------------------------
// Signed in
// ---------------------------------------------------------------------------------------------------------------------

/**
 * A new password for the signed-in account (this browser stays signed in; GoTrue signs out the account's other
 * sessions). Unlike the sign-up save, "same password" is an error here: the person asked for a new one.
 */
export async function changePassword(newPassword: string): Promise<void> {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
  // The person chose this one: a sign-up password still waiting to be saved must not replace it later.
  clearVerification();
}

/**
 * An ID token from Sign in with Apple or Google Identity Services. `rawNonce` is the value whose SHA-256 went into
 * the provider's request; GoTrue hashes it and compares it with the token's `nonce` claim.
 */
export async function signInWithIdToken(
  provider: 'google' | 'apple',
  token: string,
  rawNonce: string,
  sharedName?: { first: string; last: string } | null,
): Promise<void> {
  const { data, error } = await supabase.auth.signInWithIdToken({ provider, token, nonce: rawNonce });
  if (error) throw error;
  if (sharedName && data.user) setSharedProfileName({ ...sharedName, userId: data.user.id });
}
